// Dunning demo pay page: /d/:token
//
// The link Sophia texts during a call or in a reminder. The patient picks
// (or confirms) pay in full / a partial amount / a payment plan, then enters the
// card in Rainforest's own component, so the card never touches our code.
// Self-contained: delete this folder + its route in App.js to remove the demo.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { PAYMENTS_BASE_URL } from '../services/common';
// Served by the payments site at /d/<token>; the API is the payments server. Payments
// are sandbox (the demo's merchant is a sandbox merchant), so no real money moves.
const DEMO_API = process.env.REACT_APP_DUNNING_DEMO_API_URL || PAYMENTS_BASE_URL;
const API = `${DEMO_API}/api/dunning-demo/pay`;

const money = (cents) =>
  (Number(cents || 0) / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: Number(cents || 0) % 100 ? 2 : 0,
  });

function useRainforestScripts(sandbox) {
  useEffect(() => {
    if (sandbox === null) return undefined;
    const prefix = sandbox ? 'sandbox.' : '';
    const added = [];
    [`${prefix}merchant.js`, `${prefix}payment.js`].forEach((name) => {
      const id = `rainforest-${name}`;
      if (!document.getElementById(id)) {
        const el = document.createElement('script');
        el.id = id;
        el.type = 'module';
        el.src = `https://static.rainforestpay.com/${name}`;
        document.head.appendChild(el);
        added.push(el);
      }
    });
    return () => added.forEach((el) => el.remove());
  }, [sandbox]);
}

// The regular link offers: pay in full, build-your-own payment plan (their down payment,
// 3–24 months), and "pay part today" (any amount, listed last). The partial link Sophia
// sends (?mode=partial&amount=…) shows only that option.
const PLAN_MONTHS = [3, 6, 9, 12, 18, 24];
const PLAN_MODES = ['plan_custom', 'plan_short', 'plan_long'];

function optionList(info, partialLink) {
  const o = info.offers || {};
  const partialOption = { mode: 'partial', title: 'Pay part today', today: null,
    detail: "Pay any amount today. We'll follow up on the rest." };
  if (partialLink) return [partialOption];
  const list = [
    { mode: 'full', title: 'Pay in full', today: o.full?.amountCents, detail: 'Clears the balance today.' },
  ];
  if (o.plan_short) {   // plans exist for balances above the practice's plan threshold
    list.push({ mode: 'plan_custom', title: 'Build your payment plan', today: null,
      detail: 'Choose your down payment and how many months to spread the rest over.' });
  }
  list.push(partialOption);
  return list;
}

// Where the plan builder starts: the link's own terms (Sophia's custom or preset plan),
// otherwise the practice's suggested plan.
function initialPlan(params, offers) {
  const mode = params.get('mode');
  const preset = offers?.[mode === 'plan_long' ? 'plan_long' : 'plan_short'];
  if (mode === 'plan_custom' && params.get('amount')) {
    return { down: String(Number(params.get('amount')) / 100), months: Number(params.get('months')) || 6 };
  }
  return { down: preset ? String(preset.downCents / 100) : '', months: preset?.months || 6 };
}

export default function DunningPayPage() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [mode, setMode] = useState(
    PLAN_MODES.includes(params.get('mode')) ? 'plan_custom' : (params.get('mode') || 'full'),
  );
  const [planDown, setPlanDown] = useState('');
  const [planMonths, setPlanMonths] = useState(6);
  const [partial, setPartial] = useState(
    params.get('amount') ? String(Number(params.get('amount')) / 100) : '',
  );
  const [checkout, setCheckout] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [showLedger, setShowLedger] = useState(false);
  // Card or bank account (ACH), chosen by us above the processor's form: on phones its own
  // method switcher shows the bank option as just an icon and "•••" (Jade, 2026-10-22).
  const [payMethod, setPayMethod] = useState('CARD');
  const componentRef = useRef(null);

  useRainforestScripts(checkout ? checkout.sandbox : null);

  useEffect(() => {
    fetch(`${API}/${token}`)
      .then((r) => r.json())
      .then((body) => (body.success ? setInfo(body) : setError(body.error || 'Link not found')))
      .catch(() => setError('We could not load this link. Please try again.'));
  }, [token]);

  useEffect(() => {
    if (!info) return;
    const start = initialPlan(params, info.offers);
    setPlanDown(start.down);
    setPlanMonths(start.months);
  }, [info, params]);

  const remainingCents = info?.balance?.remainingCents || 0;
  const downCents = Math.round(Number(planDown || 0) * 100);
  const monthlyCents = planMonths ? Math.round(Math.max(remainingCents - downCents, 0) / planMonths) : 0;

  const partialLink = params.get('mode') === 'partial';
  const options = useMemo(() => (info ? optionList(info, partialLink) : []), [info, partialLink]);

  const startCheckout = async () => {
    let amountCents;
    let months;
    if (mode === 'partial') {
      amountCents = Math.round(Number(partial) * 100);
      if (!(amountCents > 0)) { setError('Enter the amount you want to pay.'); return; }
    } else if (mode === 'plan_custom') {
      amountCents = downCents;
      months = planMonths;
      if (!(downCents > 0)) { setError('Enter a down payment.'); return; }
      if (downCents >= remainingCents) { setError('That covers the whole balance. Choose "Pay in full" instead.'); return; }
    }
    setBusy(true);
    setError('');
    try {
      const resp = await fetch(`${API}/${token}/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode, amountCents, months }),
      });
      const body = await resp.json();
      if (!body.success) throw new Error(body.error || 'Payment is unavailable right now.');
      setCheckout(body);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const comp = componentRef.current;
    if (!checkout || !comp) return undefined;
    const onApproved = async (e) => {
      const envelope = Array.isArray(e?.detail) ? e.detail[0] : e?.detail;
      const data = (envelope && envelope.data) || envelope || {};
      const payinId = data.payin_id || data.payinId || data.id;
      try {
        const resp = await fetch(`${API}/${token}/complete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ payinId }),
        });
        const body = await resp.json();
        if (!body.success) throw new Error(body.error);
        setDone(body);
      } catch (err) {
        setError('Your payment went through, but we could not update your account yet. The office will confirm.');
      }
    };
    const onDeclined = () => setError('That card was declined. Try another card.');
    comp.addEventListener('approved', onApproved);
    comp.addEventListener('declined', onDeclined);
    return () => {
      comp.removeEventListener('approved', onApproved);
      comp.removeEventListener('declined', onDeclined);
    };
  }, [checkout, token, payMethod]);

  if (error && !info) {
    return <Shell><p style={{ color: 'var(--pm-danger)' }}>{error}</p></Shell>;
  }
  if (!info) return <Shell><p style={{ color: 'var(--pm-muted)' }}>Loading…</p></Shell>;

  const remaining = info.balance?.remainingCents || 0;
  const settled = done || info.status === 'paid' || info.status === 'plan';

  return (
    <Shell practice={info.practiceName} brand={info.brand}>
      <p style={{ margin: 0, color: 'var(--pm-muted)' }}>Hi {info.firstName},</p>

      {settled ? (
        <div style={{ marginTop: 12 }}>
          <h1 style={styles.h1}>You're all set ✓</h1>
          {(done?.plan || info.plan) ? (
            <p style={styles.p}>
              Your plan is active: {(done?.plan || info.plan).months} monthly payments of{' '}
              {money((done?.plan || info.plan).monthlyCents)} on the card you just used.
            </p>
          ) : (
            <p style={styles.p}>
              Remaining balance: <strong>{money(done ? done.balance.remainingCents : remaining)}</strong>
            </p>
          )}
          <p style={styles.small}>A confirmation text is on its way. Thank you!</p>
        </div>
      ) : (
        <>
          <h1 style={styles.h1}>{money(remaining)} is past due</h1>
          <button type="button" style={styles.link} onClick={() => setShowLedger((v) => !v)}>
            {showLedger ? 'Hide' : 'See'} what this is for
          </button>
          {showLedger && (
            <div style={styles.ledger}>
              {(info.ledger || []).map((line) => (
                <div key={line.date + line.description} style={styles.ledgerRow}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{line.description}</div>
                    <div style={styles.small}>
                      {line.date} · billed {money(line.billedCents)} · insurance paid {money(line.insurancePaidCents)}
                      {line.note ? ` · ${line.note}` : ''}
                    </div>
                  </div>
                  <div style={{ fontWeight: 600 }}>{money(line.patientCents)}</div>
                </div>
              ))}
            </div>
          )}

          {!checkout ? (
            <>
              <div style={{ display: 'grid', gap: 10, marginTop: 18 }}>
                {options.map((opt) => {
                  const active = opt.mode === mode;
                  return (
                    <label key={opt.mode} style={{ ...styles.option, ...(active ? styles.optionActive : {}) }}>
                      <input type="radio" name="mode" checked={active} onChange={() => setMode(opt.mode)}
                        style={{ marginTop: 3 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                          <strong>{opt.title}</strong>
                          {opt.today != null && <span>{money(opt.today)} today</span>}
                        </div>
                        <div style={styles.small}>{opt.detail}</div>
                        {opt.mode === 'plan_custom' && active && (
                          <div style={{ marginTop: 10 }}>
                            <div style={styles.small}>Down payment today</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                              <span>$</span>
                              <input type="number" inputMode="decimal" min="1" value={planDown}
                                onChange={(e) => setPlanDown(e.target.value)} style={styles.input} />
                            </div>
                            <div style={{ ...styles.small, marginTop: 10 }}>Spread the rest over</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                              {PLAN_MONTHS.map((m) => (
                                <button key={m} type="button" onClick={(e) => { e.preventDefault(); setPlanMonths(m); }}
                                  style={{ ...styles.month, ...(m === planMonths ? styles.monthActive : {}) }}>
                                  {m} mo
                                </button>
                              ))}
                            </div>
                            {downCents > 0 && downCents < remainingCents && (
                              <div style={{ marginTop: 10, fontWeight: 600 }}>
                                {money(downCents)} today, then {planMonths} × {money(monthlyCents)}/month
                              </div>
                            )}
                          </div>
                        )}
                        {opt.mode === 'partial' && active && (
                          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span>$</span>
                            <input type="number" inputMode="decimal" min="1" value={partial}
                              onChange={(e) => setPartial(e.target.value)} style={styles.input} />
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
              {error && <p style={{ color: 'var(--pm-danger)', marginTop: 12 }}>{error}</p>}
              <button type="button" className="pm-btn-primary" style={styles.cta} disabled={busy}
                onClick={startCheckout}>
                {busy ? 'One moment…' : 'Continue to secure payment'}
              </button>
            </>
          ) : (
            <div style={{ marginTop: 18 }}>
              <p style={styles.p}>
                Paying <strong>{money(checkout.quote.todayCents)}</strong> today
                {checkout.quote.plan
                  ? `, then ${checkout.quote.plan.months} × ${money(checkout.quote.plan.monthlyCents)} monthly`
                  : ''}.
              </p>
              <div style={styles.payWith}>Pay with</div>
              <div style={styles.methods} role="radiogroup" aria-label="Payment method">
                {[['CARD', '💳 Card', 'Credit or debit'], ['ACH', '🏦 Bank account (ACH)', 'Checking or savings']]
                  .map(([value, label, hint]) => (
                    <button key={value} type="button" role="radio" aria-checked={payMethod === value}
                      onClick={() => setPayMethod(value)}
                      style={{ ...styles.method, ...(payMethod === value ? styles.methodActive : {}) }}>
                      <span style={{ fontWeight: 700 }}>{label}</span>
                      <span style={{ fontSize: '0.78rem', opacity: 0.8 }}>{hint}</span>
                    </button>
                  ))}
              </div>
              {checkout.sandbox && (
                <p style={styles.small}>
                  Test mode: {payMethod === 'ACH'
                    ? 'use the test bank account option in the form.'
                    : 'pick a card from "Test card" above the card number.'}
                </p>
              )}
              {/* Remount per method so the form shows only that method. */}
              <rainforest-payment
                key={payMethod}
                ref={componentRef}
                session-key={checkout.sessionKey}
                payin-config-id={checkout.payinConfigId}
                allowed-methods={payMethod}
              ></rainforest-payment>
              {error && <p style={{ color: 'var(--pm-danger)', marginTop: 12 }}>{error}</p>}
              <button type="button" style={styles.link} onClick={() => setCheckout(null)}>
                ← Choose a different option
              </button>
            </div>
          )}
        </>
      )}
    </Shell>
  );
}

// The practice's brand (demo config `brand`): logo SVG + colours, applied by overriding the
// page's own CSS variables so buttons, links and the selected option all follow it.
function brandVars(brand) {
  if (!brand) return {};
  return {
    '--pm-brand': brand.primary,
    '--pm-brand-hover': brand.primary,
    '--pm-brand-line': brand.primary,
    '--pm-brand-tint-soft': brand.tint || '#f2f6f3',
    '--pm-page': brand.background || 'var(--pm-page)',
  };
}

function Shell({ practice, brand, children }) {
  useEffect(() => {
    if (practice) document.title = `${practice} · Pay your balance`;
  }, [practice]);
  return (
    <div style={{ minHeight: '100vh', background: 'var(--pm-page)', padding: '32px 16px', ...brandVars(brand) }}>
      <div className="pm-card" style={{ maxWidth: 520, margin: '0 auto', padding: 24 }}>
        {practice && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            {brand?.logoSvg && (
              <img alt="" style={{ height: 36, width: 'auto' }}
                src={`data:image/svg+xml;utf8,${encodeURIComponent(brand.logoSvg)}`} />
            )}
            <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--pm-brand)', letterSpacing: '0.04em',
              textTransform: 'uppercase' }}>
              {practice}
            </div>
          </div>
        )}
        {children}
        <div style={{ ...styles.small, marginTop: 24, textAlign: 'center' }}>
          🔒 Card and bank details are handled by our payment processor and never stored by the office.
        </div>
      </div>
    </div>
  );
}

const styles = {
  h1: { fontSize: '1.6rem', margin: '4px 0 6px', color: 'var(--pm-ink-strong)' },
  p: { color: 'var(--pm-text)', margin: '6px 0' },
  small: { fontSize: '0.82rem', color: 'var(--pm-muted)' },
  link: { background: 'none', border: 'none', padding: 0, marginTop: 10, color: 'var(--pm-brand)',
    cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600 },
  ledger: { marginTop: 10, border: '1px solid var(--pm-line)', borderRadius: 10, overflow: 'hidden' },
  ledgerRow: { display: 'flex', justifyContent: 'space-between', gap: 12, padding: '10px 12px',
    borderBottom: '1px solid var(--pm-line-soft)', fontSize: '0.9rem' },
  option: { display: 'flex', gap: 10, alignItems: 'flex-start', padding: '12px 14px', borderRadius: 10,
    border: '1px solid var(--pm-line)', cursor: 'pointer', background: 'var(--pm-surface)' },
  optionActive: { borderColor: 'var(--pm-brand-line)', background: 'var(--pm-brand-tint-soft)' },
  input: { width: 120, padding: '6px 8px', borderRadius: 8, border: '1px solid var(--pm-line-input)' },
  month: { padding: '6px 12px', borderRadius: 999, border: '1px solid var(--pm-line)', background: 'var(--pm-surface)',
    cursor: 'pointer', fontSize: '0.85rem' },
  monthActive: { borderColor: 'var(--pm-brand)', background: 'var(--pm-brand)', color: '#fff' },
  cta: { width: '100%', marginTop: 18, padding: '12px 16px', fontSize: '1rem' },
  payWith: { fontSize: '0.85rem', fontWeight: 700, color: 'var(--pm-text)', margin: '14px 0 8px' },
  methods: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 },
  method: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, padding: '10px 12px',
    borderRadius: 10, border: '1px solid var(--pm-line)', background: 'var(--pm-surface)', cursor: 'pointer',
    textAlign: 'left', color: 'var(--pm-ink-strong)', fontSize: '0.92rem' },
  methodActive: { borderColor: 'var(--pm-brand)', background: 'var(--pm-brand-tint-soft)',
    boxShadow: 'inset 0 0 0 1px var(--pm-brand)' },
};
